import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { LendioDocusignEnvelopeEventReport } from '@app/app/interfaces/docusign-models';
import { DisplayableEvent } from './process-timestamps-models';

@Component({
  selector: 'app-process-timestamps-tooltip',
  imports: [CommonModule, MatIconModule],
  templateUrl: './process-timestamps-tooltip.component.html',
  styleUrl: './process-timestamps-tooltip.component.scss'
})
export class ProcessTimestampsTooltipComponent {
  @Input() tooltipTitle: string;
  @Input() timestamps: LendioDocusignEnvelopeEventReport = {
    created: null,
    sent: null,
    delivered: null,
    completed: null,
    corrected: null,
    declined: null,
    voided: null,
    expired: null
  };

  processedEvents: DisplayableEvent[] = [];
  completableStatuses = ['created', 'sent', 'delivered', 'completed'];
  errorStatuses = ['declined', 'voided', 'expired'];

  ngOnInit() {
    this.processedEvents = this.handleEvents();
  }

  createDisplayableEvent(label: string, timestamp: number | null): DisplayableEvent {
    let status: 'incomplete' | 'complete' | 'error' = 'incomplete';
    if (this.errorStatuses.includes(label)) {
      status = 'error'
    }
    if (this.completableStatuses.includes(label) && timestamp) {
      status = 'complete'
    }
    return { label, timestamp, status }
  }

  handleEvents() {
    let displayableEvents: DisplayableEvent[] = []
    const { created, sent, delivered, completed, corrected, declined, voided, expired } = this.timestamps;

    // See SAAS-2593 spec, requirement 4.
    // show the happy path - created, sent, delivered, completed
    displayableEvents.push(
      this.createDisplayableEvent('created', created),
      this.createDisplayableEvent('sent', sent),
      this.createDisplayableEvent('delivered', delivered),
      this.createDisplayableEvent('completed', completed)
    )

    // Filter out of the list any happy path statuses that are incomplete but that precede another completed event.
    // We are going to assume: they happened, but we didn't get the webhook for them yet.
    // Since we don't have a timestamp to show though, don't show them in the tooltip.
    displayableEvents = displayableEvents.filter((thisEvent, i, arr) => {
      const nextEvent = arr[i + 1];
      if (!nextEvent) {
        return true
      } else {
        if (thisEvent.status === "incomplete" && nextEvent.status === "complete") {
          return false
        }
      }
      return true
    })

    // Only insert "corrected" event if we have a timestamp for it, 
    // since it's not a happy-path event
    if (corrected) {
      displayableEvents.push(
        this.createDisplayableEvent('corrected', corrected)
      )
    }

    // Error events, if present, go at the bottom of the list.
    if (declined) {
      displayableEvents.push(
        this.createDisplayableEvent('declined', declined)
      )
    }

    if (voided) {
      displayableEvents.push(
        this.createDisplayableEvent('voided', voided)
      )
    }

    if (expired) {
      displayableEvents.push(
        this.createDisplayableEvent('expired', expired)
      )
    }

    // If error events, only show events for which we want a timestamp
    if (declined || voided || expired) {
      displayableEvents = displayableEvents.filter(event => event.timestamp)
    }

    return displayableEvents;
  }
}
